import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, can, getUser } from '../lib/api';
import { bytes, date, dateTime, day, retention } from '../lib/format';
import { Card, Empty, ErrorNote, Field, Hash, Loading, PageHead, Pill, useResource, useToast } from '../components/ui';
import '../styles/principals.css';
import { RevealedField, useReveal } from '../components/Controls';
import '../styles/nominations.css';
import {
  ACTIVATION_LABEL, ACTIVATION_TONE, NOMINATION_LABEL, NOMINATION_TONE, RELATIONSHIP_LABEL, TRIGGER_LABEL, type Nomination,
} from './Nominations';

/**
 * Data principals: one person's record, looked up by identifier and shown by
 * hash. The identifier is never kept on screen; it goes into the hash and is
 * discarded. The signed summary is the document a S.11 request is answered with.
 */

interface Identity {
  identifierType: string | null;
  hashed: true;
  hashPrefix: string;
  firstSeen: string | null;
  lastSeen: string | null;
  linkedBrowsers: { count: number; list: Array<{ hashPrefix: string; via: string; linkedAt: string; firstSeen: string | null; lastSeen: string | null }> };
  alsoReachedBy: Array<{ identifierType: string; hashPrefix: string; via: string; linkedAt: string }>;
  ipStored: false;
  deviceRead: false;
}

interface Record_ {
  principalHash: string;
  identifierType: string | null;
  firstSeen: string | null;
  lastSeen: string | null;
  identity: Identity;
  purposes: Array<{
    purposeId: string; name: string; basis: 'consent' | 'legitimate_use'; basisSubtype: string | null;
    dataCategories: string[]; retentionDays: number | null; processors: string[]; requiredForService: boolean;
    decision: string; live: boolean; decidedAt: string | null; expiresAt: string | null; noticeVersion: number | null; lang: string | null;
  }>;
  processorsTold: Array<{ name: string; deliveries: number; delivered: number; lastAt: string | null }>;
  notices: Array<{ noticeId: string; title: string; version: number; lang: string; firstShownAt: string }>;
  campaigns: Array<{ campaignId: string; name: string; status: string; contactCount: number; sentAt: string | null; respondedAt: string | null }>;
  requests: Array<{ id: string; reference: string; kind: string; status: string; receivedAt: string; dueBy: string; closedAt: string | null; outcome: string | null }>;
  erasures?: Array<{
    requestId: string; reference: string; status: string; takenUpAt: string | null;
    processorsConfirmed: number; processorsOutstanding: number;
    proof: { systems: string[]; method: string; erasedOn: string; confirmedBy: string; overrideReason: string | null } | null;
  }>;
  events: Array<{ event_id: string; seq: number; purpose_id: string | null; action: string; channel: string; occurred_at: string; notice_version: number | null; lang: string | null; evidence: Record<string, unknown> }>;
}

const DECISION: Record<string, { label: string; tone?: 'ok' | 'warn' | 'bad' | 'brand' }> = {
  granted: { label: 'Consent given', tone: 'ok' }, refreshed: { label: 'Renewed', tone: 'ok' },
  declined: { label: 'Declined', tone: 'bad' }, withdrawn: { label: 'Withdrawn', tone: 'warn' },
  expired: { label: 'Expired', tone: 'warn' }, informed: { label: 'Notified' },
  requested: { label: 'Asked' }, re_requested: { label: 'Asked again' },
};

const KIND: Record<string, string> = { access: 'Access', correction: 'Correction', erasure: 'Erasure', nomination: 'Nomination (legacy)', nomination_activation: 'Nominee activation', grievance: 'Grievance' };
const ID_TYPE: Record<string, string> = { email: 'Email address', phone: 'Mobile number', customer_id: 'Customer ID', visitor_id: 'Browser cookie id' };
const LINK_VIA: Record<string, string> = { form: 'Form', login: 'Login', preference_centre: 'Preference centre', api: 'Integration', operator: 'Operator' };

export default function Principals() {
  const { hash } = useParams();
  return hash ? <PrincipalRecord hash={hash} /> : <Lookup />;
}

const CHANNEL: Record<string, string> = {
  web: 'Website', app: 'Mobile app', api: 'Integration', cookie: 'Cookie banner', pref_centre: 'Preference centre',
  refresh_campaign: 'Campaign', rights_request: 'Rights request', system: 'Expiry', offline: 'In person',
  callcentre: 'Call centre', paper: 'Paper form', kiosk: 'Kiosk', ivr: 'Phone (IVR)', email: 'Email', sms: 'SMS', whatsapp: 'WhatsApp',
};

type IdType = 'email' | 'phone' | 'customer_id';

/** An @ is an email address; ten or more digits are a mobile number; anything else is a customer ID. */
function detectType(v: string): IdType {
  const t = v.trim();
  if (t.includes('@')) return 'email';
  if (/^\+?[\d\s-]{10,}$/.test(t)) return 'phone';
  return 'customer_id';
}

interface Recent {
  totals: { people: number; live: number; changed30: number; guarded: number };
  recent: Array<{ principalHash: string; action: string; channel: string; occurredAt: string; purpose: string | null; livePurposes: number; guarded: boolean }>;
}

const FILTERS: Array<[string, string]> = [['', 'All'], ['granted', 'Consent given'], ['withdrawn', 'Withdrawn'], ['declined', 'Declined'], ['expired', 'Expired']];

function Lookup() {
  const navigate = useNavigate();
  const toast = useToast();
  const [identifier, setIdentifier] = useState('');
  const [type, setType] = useState<IdType>('email');
  const [typeChosen, setTypeChosen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState(false);
  const [filter, setFilter] = useState('');
  const recent = useResource<Recent>(() => api.get(`/v1/principals/recent?limit=25${filter ? `&action=${filter}` : ''}`), [filter]);

  async function find() {
    if (!identifier.trim()) return;
    setBusy(true);
    setMissing(false);
    try {
      const res = await api.get<{ principalHash: string; found: boolean; via?: 'alias' }>(`/v1/principals/lookup?identifier=${encodeURIComponent(identifier.trim())}&type=${type}`);
      if (res.found && res.via === 'alias') toast('This identifier is joined to a record kept under another identifier.', 'ok');
      if (res.found) navigate(`/principals/${res.principalHash}`);
      else setMissing(true);
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  const t = recent.data?.totals;
  const rows = recent.data?.recent ?? [];

  return (
    <>
      <PageHead title="Data principals" />
      <form className="lk-bar" onSubmit={(e) => { e.preventDefault(); find(); }}>
        <select
          className="lk-type" aria-label="Identifier type" value={type}
          onChange={(e) => { setType(e.target.value as IdType); setTypeChosen(true); }}
        >
          <option value="email">Email address</option>
          <option value="phone">Mobile number</option>
          <option value="customer_id">Customer ID</option>
        </select>
        <input
          className="lk-input" type="text" autoComplete="off" value={identifier}
          placeholder="Email address, mobile number or customer ID" aria-label="Identifier"
          title="Hashed on entry. Not stored or logged."
          onChange={(e) => {
            setIdentifier(e.target.value);
            setMissing(false);
            if (!typeChosen) setType(detectType(e.target.value));
          }}
        />
        <button className="primary" type="submit" disabled={busy}>{busy ? 'Looking up…' : 'Open record'}</button>
      </form>
      <div className="lk-meta">
        {missing ? <span className="lk-missing">No record for this identifier.</span> : <span>Hashed on entry. Not stored or logged.</span>}
      </div>

      {t && (
        <p className="page-line">
          <strong>{t.people.toLocaleString('en-IN')}</strong> people on record · {t.live.toLocaleString('en-IN')} with live consent
          · {t.changed30.toLocaleString('en-IN')} changed in 30 days{t.guarded ? <> · {t.guarded} children or under guardianship</> : null}
        </p>
      )}

      <div className="tabs">
        {FILTERS.map(([k, label]) => (
          <button key={k || 'all'} className={filter === k ? 'active' : ''} onClick={() => setFilter(k)}>{label}</button>
        ))}
      </div>

      <Card>
        <ErrorNote error={recent.error} />
        {recent.loading && !recent.data && <Loading what="Loading recent changes" />}
        {recent.data && rows.length === 0 && <Empty>No changes.</Empty>}
        {rows.length > 0 && (
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr><th>Changed</th><th>Record</th><th>Latest change</th><th>Channel</th><th className="num">Live purposes</th><th /></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.principalHash} onClick={() => navigate(`/principals/${r.principalHash}`)} style={{ cursor: 'pointer' }}>
                    <td className="small">{day(r.occurredAt)}</td>
                    <td><Hash value={r.principalHash} />{r.guarded && <> <Pill tag>Child</Pill></>}</td>
                    <td>
                      <Pill tone={DECISION[r.action]?.tone}>{DECISION[r.action]?.label ?? r.action}</Pill>
                      {r.purpose && <span className="lk-purpose"> {r.purpose}</span>}
                    </td>
                    <td className="small">{CHANNEL[r.channel] ?? r.channel}</td>
                    <td className="num">{r.livePurposes}</td>
                    <td className="row-actions"><Link to={`/principals/${r.principalHash}`} onClick={(e) => e.stopPropagation()}>Open</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

function PrincipalRecord({ hash }: { hash: string }) {
  const toast = useToast();
  const [downloading, setDownloading] = useState(false);
  const canExport = can(getUser(), 'principals:export');
  const loaded = useResource<{ record: Record_ }>(() => api.get(`/v1/principals/${hash}`), [hash]);
  const reveal = useReveal('principal', hash);

  async function exportSummary() {
    setDownloading(true);
    try {
      await api.download(`/v1/principals/${hash}/summary.pdf`, `personal-data-summary-${hash.slice(0, 12)}.pdf`);
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setDownloading(false);
    }
  }

  /** The per-consent evidence pack (wk.consent.evidence.v1), signed; the PDF carries the JSON's bundle hash. */
  async function evidencePack(purposeId: string, purposeName: string, format: 'pdf' | 'json') {
    try {
      await api.download(
        `/v1/consents/${hash}/${purposeId}/evidence${format === 'pdf' ? '.pdf' : ''}`,
        `consent-evidence-${hash.slice(0, 12)}-${purposeName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40)}.${format}`,
      );
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  if (loaded.loading && !loaded.data) return <Loading what="Loading the record" />;
  if (loaded.error) return <><PageHead eyebrow="Data principals" backTo="/principals" title="Record" /><ErrorNote error={loaded.error} /></>;
  if (!loaded.data) return null;
  const r = loaded.data.record;
  const live = r.purposes.filter((p) => p.live && p.basis === 'consent').length;
  const withdrawn = r.purposes.filter((p) => p.decision === 'withdrawn').length;
  const purposeName = (id: string | null) => r.purposes.find((p) => p.purposeId === id)?.name ?? '';

  return (
    <>
      <PageHead eyebrow="Data principals" backTo="/principals" title={`${ID_TYPE[r.identifierType ?? ''] ?? 'Record'}, hashed`}>
        <Link className="btn" to="/principals">Look up another</Link>
        {canExport && (
          <button className="primary" onClick={exportSummary} disabled={downloading}>{downloading ? 'Preparing…' : 'Evidence sheet'}</button>
        )}
      </PageHead>

      <div className="stat-row">
        <Card><div className="stat"><h3>Live consents</h3><div className="value">{live}</div></div></Card>
        <Card><div className="stat"><h3>Withdrawn</h3><div className="value">{withdrawn}</div></div></Card>
        <Card><div className="stat"><h3>Processors told</h3><div className="value">{r.processorsTold.length}</div></div></Card>
        <Card><div className="stat"><h3>Requests</h3><div className="value">{r.requests.length}</div><div className="sub">{r.requests.filter((q) => q.status === 'open' || q.status === 'acknowledged').length ? `${r.requests.filter((q) => q.status === 'open' || q.status === 'acknowledged').length} open` : ''}</div></div></Card>
      </div>

      <Card title="How this record identifies the person">
        <dl className="kv">
          <dt>Identifier type</dt><dd>{ID_TYPE[r.identity.identifierType ?? ''] ?? r.identity.identifierType ?? 'Unknown'}</dd>
          <dt>Stored as</dt><dd>A hash of the identifier, not the value</dd>
          <dt>Contact</dt><dd><RevealedField reveal={reveal} value={reveal.values?.identifier} />{reveal.dialog}</dd>
          <dt>Reference</dt><dd><span className="mono">{r.identity.hashPrefix}</span> <span className="muted">hashed</span></dd>
          <dt>First seen</dt><dd>{date(r.identity.firstSeen)}</dd>
          <dt>Last seen</dt><dd>{date(r.identity.lastSeen)}</dd>
          <dt>Also reached by</dt>
          <dd>
            {r.identity.alsoReachedBy.length === 0 ? 'No other identifier' : (
              <div className="small">
                {r.identity.alsoReachedBy.map((a) => (
                  <div key={a.hashPrefix}>
                    {ID_TYPE[a.identifierType] ?? a.identifierType} <span className="mono">{a.hashPrefix}</span> <span className="muted">hashed · {LINK_VIA[a.via] ?? a.via}, {date(a.linkedAt)}</span>
                  </div>
                ))}
              </div>
            )}
          </dd>
          <dt>Browsers linked</dt>
          <dd>
            {r.identity.linkedBrowsers.count}
            {r.identity.linkedBrowsers.list.length > 0 && (
              <div className="small muted">
                {r.identity.linkedBrowsers.list.map((b) => (
                  <div key={b.hashPrefix}>
                    <span className="mono">{b.hashPrefix}</span> hashed · {LINK_VIA[b.via] ?? b.via}, {date(b.linkedAt)} · seen {date(b.firstSeen)} to {date(b.lastSeen)}
                  </div>
                ))}
              </div>
            )}
          </dd>
          <dt>IP address</dt><dd>{r.identity.ipStored ? 'Stored' : 'Not stored'}</dd>
          <dt>Device</dt><dd>{r.identity.deviceRead ? 'Read' : 'Not read'}</dd>
        </dl>
        {canExport && <div className="small muted" style={{ marginTop: 10 }}>The evidence sheet lists every event on this record with its notice version, banner version, hashes and how the person was identified.</div>}
      </Card>

      <Card title="Personal data and purposes">
        {r.purposes.length === 0 ? <Empty>No purposes on record.</Empty> : (
          <div className="table-scroll">
            <table className="data">
              <thead><tr><th>Purpose</th><th>Personal data</th><th>Basis</th><th>Decision</th><th>Kept until</th><th>Shared with</th><th>Notice</th>{canExport && <th>Evidence pack</th>}</tr></thead>
              <tbody>
                {r.purposes.map((p) => (
                  <tr key={p.purposeId}>
                    <td><strong>{p.name}</strong>{p.requiredForService && <div className="small muted">Needed to provide the service</div>}</td>
                    <td className="small">{p.dataCategories.join(', ')}</td>
                    <td className="small">{p.basis === 'consent' ? 'Consent (S.6)' : p.basisSubtype ?? 'Legitimate use (S.7)'}</td>
                    <td>
                      <Pill tone={DECISION[p.decision]?.tone}>{DECISION[p.decision]?.label ?? p.decision}</Pill>
                      <div className="small muted">{date(p.decidedAt)}</div>
                    </td>
                    <td className="small">{p.expiresAt ? date(p.expiresAt) : retention(p.retentionDays)}</td>
                    <td className="small">{p.processors.join(', ') || '—'}</td>
                    <td className="small muted">{p.noticeVersion ? `v${p.noticeVersion} · ${p.lang}` : ''}</td>
                    {canExport && (
                      <td className="u-actions" style={{ whiteSpace: 'nowrap' }}>
                        <button className="small" onClick={() => evidencePack(p.purposeId, p.name, 'pdf')}>PDF</button>{' '}
                        <button className="small" onClick={() => evidencePack(p.purposeId, p.name, 'json')}>JSON</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="grid cols-2" style={{ alignItems: 'start' }}>
        <div>
          <Card title="Processors told">
            {r.processorsTold.length === 0 ? <Empty>None.</Empty> : (
              <table className="data">
                <thead><tr><th>Recipient</th><th className="num">Notifications</th><th className="num">Delivered</th><th>Last</th></tr></thead>
                <tbody>{r.processorsTold.map((t) => <tr key={t.name}><td>{t.name}</td><td className="num">{t.deliveries}</td><td className="num">{t.delivered}</td><td className="small muted">{dateTime(t.lastAt)}</td></tr>)}</tbody>
              </table>
            )}
          </Card>
          <Card title="Notices shown">
            {r.notices.length === 0 ? <Empty>None.</Empty> : (
              <table className="data">
                <thead><tr><th>Notice</th><th>Version</th><th>Language</th><th>First shown</th></tr></thead>
                <tbody>{r.notices.map((n) => <tr key={`${n.noticeId}-${n.version}-${n.lang}`}><td><Link to={`/notices/${n.noticeId}/versions/${n.version}`}>{n.title}</Link></td><td>v{n.version}</td><td>{n.lang}</td><td className="small muted">{dateTime(n.firstShownAt)}</td></tr>)}</tbody>
              </table>
            )}
          </Card>
          <Card title="Campaign contacts">
            {r.campaigns.length === 0 ? <Empty>None.</Empty> : (
              <table className="data">
                <thead><tr><th>Campaign</th><th>State</th><th className="num">Contacts</th><th>Sent</th><th>Responded</th></tr></thead>
                <tbody>{r.campaigns.map((c) => <tr key={c.campaignId}><td><Link to={`/campaigns/${c.campaignId}`}>{c.name}</Link></td><td className="small">{c.status}</td><td className="num">{c.contactCount}</td><td className="small muted">{date(c.sentAt)}</td><td className="small muted">{date(c.respondedAt)}</td></tr>)}</tbody>
              </table>
            )}
          </Card>
        </div>
        <div>
          <NomineesPanel hash={hash} />
          <OperatorFiles hash={hash} />
          <Card title="Rights requests">
            {r.requests.length === 0 ? <Empty>None.</Empty> : (
              <table className="data">
                <thead><tr><th>Reference</th><th>Kind</th><th>Received</th><th>Due</th><th>Status</th></tr></thead>
                <tbody>{r.requests.map((q) => <tr key={q.id}><td><Link to={q.kind === 'nomination_activation' ? `/nominations?ref=${encodeURIComponent(q.reference)}` : `/requests?ref=${encodeURIComponent(q.reference)}`}>{q.reference}</Link></td><td>{KIND[q.kind] ?? q.kind}</td><td className="small muted">{date(q.receivedAt)}</td><td className="small">{q.dueBy}</td><td><Pill tone={q.status === 'completed' ? 'ok' : q.status === 'rejected' ? 'bad' : 'warn'}>{q.status}</Pill></td></tr>)}</tbody>
              </table>
            )}
          </Card>
          {(r.erasures?.length ?? 0) > 0 && (
            <Card title="Erasure">
              <table className="data">
                <thead><tr><th>Reference</th><th>Processors</th><th>Erased from</th><th>Erased on</th></tr></thead>
                <tbody>{r.erasures!.map((x) => (
                  <tr key={x.requestId}>
                    <td><Link to={`/requests?ref=${encodeURIComponent(x.reference)}`}>{x.reference}</Link></td>
                    <td className="small">{x.processorsConfirmed} of {x.processorsConfirmed + x.processorsOutstanding} confirmed</td>
                    <td className="small">{x.proof ? x.proof.systems.join(', ') : <span className="muted">Not yet erased</span>}</td>
                    <td className="small">{x.proof?.erasedOn ?? '—'}</td>
                  </tr>
                ))}</tbody>
              </table>
            </Card>
          )}
          <Card title={`History · ${r.events.length}`}>
            <div className="table-scroll" style={{ maxHeight: 420, overflowY: 'auto' }}>
              <table className="data">
                <thead><tr><th>When</th><th>Event</th><th>Purpose</th><th>Channel</th></tr></thead>
                <tbody>
                  {r.events.map((e) => (
                    <tr key={e.event_id}>
                      <td className="small muted">{dateTime(e.occurred_at)}</td>
                      <td>
                        {DECISION[e.action]?.label ?? e.action.replace(/_/g, ' ')}{e.channel === 'nominee' && <> <Pill tag>Nominee</Pill></>}
                        {e.evidence?.surface === 'operator' && (
                          <div className="small muted">
                            Recorded by {String(e.evidence.operator ?? '')}
                            {typeof e.evidence.formRef === 'string' ? ` · ${e.evidence.formRef}` : ''}
                            {e.evidence.verification === 'otp_voice' ? ' · code read back' : e.evidence.verification === 'none' ? ' · attested, no code' : ''}
                          </div>
                        )}
                      </td>
                      <td className="small">{purposeName(e.purpose_id)}</td>
                      <td className="small muted">{CHANNEL[e.channel] ?? e.channel.replace(/_/g, ' ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          <div className="small muted" style={{ marginTop: 6 }}>Reference <span className="mono">{r.identity.hashPrefix}</span> hashed · first seen {date(r.firstSeen)} · last seen {date(r.lastSeen)}</div>
        </div>
      </div>
    </>
  );
}

interface OperatorFile { id: string; filename: string; mime: string; sizeBytes: number; sha256: string; uploadedBy: string; uploadedAt: string; collectionPointId: string | null }

/** Scanned forms and photos filed with consent recorded by an operator. */
function OperatorFiles({ hash }: { hash: string }) {
  const toast = useToast();
  const res = useResource<{ attachments: OperatorFile[] }>(() => api.get(`/v1/consents/operator/attachments?principalHash=${hash}`), [hash]);
  const rows = res.data?.attachments ?? [];
  if (!res.error && res.data && rows.length === 0) return null;
  return (
    <Card title="Signed forms">
      {res.loading && !res.data && <Loading />}
      <ErrorNote error={res.error} />
      {rows.length > 0 && (
        <table className="data">
          <thead><tr><th>File</th><th>Filed</th><th>SHA-256</th><th /></tr></thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id}>
                <td>{a.filename}<div className="small muted">{bytes(a.sizeBytes)}</div></td>
                <td className="small">{day(a.uploadedAt)}<div className="muted">{a.uploadedBy}</div></td>
                <td className="small mono" title={a.sha256}>{a.sha256.slice(0, 12)}…</td>
                <td className="u-actions"><button className="small" onClick={() => api.download(`/v1/consents/operator/attachments/${a.id}/download`, a.filename).catch((e) => toast((e as Error).message, 'bad'))}>Download</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

interface PrincipalNominations {
  nominations: Nomination[];
  activations: Array<{ id: string; reference: string; trigger: string; status: keyof typeof ACTIVATION_LABEL; noticeUntil: string | null }>;
}

/** S.14: who this person has named, and any open request by a nominee to act for them. */
function NomineesPanel({ hash }: { hash: string }) {
  const res = useResource<PrincipalNominations>(() => api.get(`/v1/principals/${hash}/nominations`), [hash]);
  const noms = res.data?.nominations ?? [];
  const acts = res.data?.activations ?? [];
  return (
    <Card title="Nominees">
      {res.loading && !res.data && <Loading />}
      <ErrorNote error={res.error} />
      {res.data && noms.length === 0 && <Empty>None.</Empty>}
      {noms.length > 0 && (
        <table className="data">
          <thead><tr><th>Relationship</th><th className="num">Rank</th><th>Contact</th><th>Status</th><th>Registered</th></tr></thead>
          <tbody>
            {noms.map((n) => (
              <tr key={n.id}>
                <td>{RELATIONSHIP_LABEL[n.relationship] ?? n.relationship}</td>
                <td className="num">{n.rank}</td>
                <td className="mono small">{n.nomineeContact || '—'}</td>
                <td><Pill tone={NOMINATION_TONE[n.status]}>{NOMINATION_LABEL[n.status] ?? n.status}</Pill></td>
                <td className="small muted">{day(n.registeredAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {acts.length > 0 && (
        <>
          <div className="nm-panel-sub">Open activations</div>
          <table className="data">
            <thead><tr><th>Reference</th><th>Trigger</th><th>Notice until</th><th>Status</th></tr></thead>
            <tbody>
              {acts.map((a) => (
                <tr key={a.id}>
                  <td><Link to={`/nominations?ref=${encodeURIComponent(a.reference)}`}>{a.reference}</Link></td>
                  <td>{TRIGGER_LABEL[a.trigger] ?? a.trigger}</td>
                  <td className="small muted">{day(a.noticeUntil)}</td>
                  <td><Pill tone={ACTIVATION_TONE[a.status]}>{ACTIVATION_LABEL[a.status] ?? a.status}</Pill></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Card>
  );
}
